import { ChevronDown, CurrencyDollarCircle, ShieldPlus, Users01, VideoRecorder } from "@untitledui/icons";
import type { ComponentType } from "react";
import { GroupedActionMenu } from "@/components/action-menu/grouped-action-menu";
import { DisabledActionMenuItem } from "@/components/assurance/disabled-action-menu-item";
import { Button } from "@/components/base/buttons/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { buildCreatorActionGroups } from "@/lib/creator-action-groups";

const ActionMenuItem = ({
  icon: Icon,
  label,
  onSelect,
  disabledReason,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onSelect: () => void;
  disabledReason?: string;
}) => {
  if (disabledReason) {
    return <DisabledActionMenuItem icon={Icon} label={label} tooltip={disabledReason} />;
  }
  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className="flex items-center gap-2 py-2"
    >
      <Icon className="w-4 h-4 text-fg-quaternary shrink-0" />
      <span className="text-secondary font-semibold">{label}</span>
    </DropdownMenuItem>
  );
};

export interface VideoCreatorActionHandlers {
  onMessageCreator: () => void;
  onAddToList: () => void;
  onInviteToCollaboration: () => void;
  onAddToCohort: () => void;
  onBlock: () => void;
  onRemoveFromCollaboration: () => void;
  cohortsEnabled: boolean;
  blocklistEnabled: boolean;
}

export const VideoActionsMenuItems = ({ handlers }: { handlers: VideoCreatorActionHandlers }) => (
  <GroupedActionMenu
    groups={buildCreatorActionGroups({
      onInvite: handlers.onInviteToCollaboration,
      onMessage: handlers.onMessageCreator,
      onAddToList: handlers.onAddToList,
      onAddToCohort: handlers.onAddToCohort,
      onBlock: handlers.onBlock,
      onRemove: handlers.onRemoveFromCollaboration,
      cohortsEnabled: handlers.cohortsEnabled,
      blocklistEnabled: handlers.blocklistEnabled,
    })}
  />
);

export const ManageCreatorDropdown = ({
  onMessageCreator,
  onAddToList,
  onInviteToCollaboration,
  onAddToCohort,
  onBlock,
  onRemoveFromCollaboration,
  cohortsEnabled,
  blocklistEnabled,
  disabled,
}: VideoCreatorActionHandlers & { disabled?: boolean }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild disabled={disabled}>
      <Button
        isDisabled={disabled}
        iconLeading={<Users01 className="w-4 h-4" />}
        iconTrailing={<ChevronDown className="w-4 h-4" />}
      >
        Manage Creator
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8}>
      <GroupedActionMenu
        groups={buildCreatorActionGroups({
          onInvite: onInviteToCollaboration,
          onMessage: onMessageCreator,
          onAddToList: onAddToList,
          onAddToCohort: onAddToCohort,
          onBlock: onBlock,
          onRemove: onRemoveFromCollaboration,
          cohortsEnabled,
          blocklistEnabled,
        })}
      />
    </DropdownMenuContent>
  </DropdownMenu>
);

export const VideoActionsDropdown = ({
  onAddRemoveAdCampaign,
  onAddRisk,
  adCampaignDisabledReason,
  disabled,
}: {
  onAddRemoveAdCampaign: () => void;
  onAddRisk: () => void;
  adCampaignDisabledReason?: string;
  disabled?: boolean;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild disabled={disabled}>
      <Button
        isDisabled={disabled}
        iconLeading={<VideoRecorder className="w-4 h-4" />}
        iconTrailing={<ChevronDown className="w-4 h-4" />}
      >
        Video
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8}>
      <ActionMenuItem
        icon={CurrencyDollarCircle}
        label="Remove from ad campaign"
        onSelect={onAddRemoveAdCampaign}
        disabledReason={adCampaignDisabledReason}
      />
      <ActionMenuItem icon={ShieldPlus} label="Add risk" onSelect={onAddRisk} />
    </DropdownMenuContent>
  </DropdownMenu>
);
